import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  HostBinding,
  Input,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { AcceptJSService } from 'src/app/services/accept-js.service';
import { AuthService } from 'src/app/services/auth.service';
import { FirebaseService } from 'src/app/services/firebase.service';
import {
  GroupedLicenseInvoiceDetails,
  DesktopInvoiceDetails,
  InvoicesService,
  ApiInvoiceDetails,
} from 'src/app/services/invoices.service';
import {
  DesktopLicenseWithProduct,
  LicensesService,
} from 'src/app/services/licenses.service';
import {
  AuthorizeNetSavedPaymentMethod,
  PaymentsService,
} from 'src/app/services/payments.service';
import { SalesRep } from 'src/app/services/sales-reps.service';
import { Tenant, TenantsService } from 'src/app/services/tenants.service';
import { environment } from 'src/environments/environment';
import { DesktopPaymentResponse } from 'src/app/services/desktop-payments.service';
import { ApiProduct } from 'src/app/services/api-products.service';
import { ApiPaymentResponse } from 'src/app/services/api-payments.service';
import { ViewService } from 'src/app/services/view.service';
import {
  DesCreditPurchase,
  DesCreditPurchasesService,
  DesPaymentResponse,
} from 'src/app/services/des-credit-purchases.service';

type GroupedLicenses = {
  license: DesktopLicenseWithProduct;
  count: number;
};

@Component({
  selector: 'app-checkout',
  templateUrl: './checkout.component.html',
  styleUrl: './checkout.component.scss',
})
export class CheckoutComponent implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding('class') class = 'flex justify-center w-full';
  @Input() invoiceId?: string;
  @Input({ required: true }) tenantId!: string;
  @Input() isAPI: boolean = false;
  @Input() isDES: boolean = false;

  @ViewChild('acceptPaymentButton')
  acceptPaymentButton!: ElementRef<HTMLButtonElement>;

  desktopInvoice?: DesktopInvoiceDetails;
  apiInvoice?: ApiInvoiceDetails;
  desPurchase?: DesCreditPurchase;
  loadingInvoice = false;
  errorFetchingInvoice = '';

  tenant?: Tenant;
  loadingTenant = false;
  errorFetchingTenant = '';

  apiProduct?: ApiProduct;
  groupedLicenseInvoiceDetails: GroupedLicenseInvoiceDetails[] = [];
  groupedNotRenewingLicenses: GroupedLicenses[] = [];

  paymentProcessing = false;
  paymentError = '';
  paymentStatusDescription = '';

  savedPaymentMethods?: AuthorizeNetSavedPaymentMethod[];
  loadingSavedPaymentMethods = false;
  errorFetchingSavedPaymentMethods = '';
  selectedSavedPaymentMethod?: AuthorizeNetSavedPaymentMethod;

  authNetAPILoginID = environment.authorizeNet.apiLoginID;
  authNetClientKey = environment.authorizeNet.clientKey;

  constructor(
    private acceptJSService: AcceptJSService,
    private authService: AuthService,
    private firebaseService: FirebaseService,
    private invoiceService: InvoicesService,
    private licensesService: LicensesService,
    private paymentsService: PaymentsService,
    private desCreditPurchasesService: DesCreditPurchasesService,
    private tenantsService: TenantsService,
    private router: Router,
    private viewService: ViewService
  ) {}

  ngOnInit() {
    window.acceptJSResponseHandler = this.acceptJSResponseHandler.bind(this);

    console.log('invoice.component invoiceId: ', this.invoiceId);
    console.log('invoice.component tenantId: ', this.tenantId);
    console.log('invoice.component isAPI: ', this.isAPI);

    if (!this.tenantId) {
      this.errorFetchingInvoice = 'Error fetching invoice';
      const err = new Error('No tenant ID found');
      console.error(err);
      return;
    }

    if (this.isDES) {
      if (!this.invoiceId) {
        this.errorFetchingInvoice = 'Error fetching DES credit purchase';
        return;
      }
      this.fetchDesPurchase(this.invoiceId, this.tenantId);
      return;
    }

    if (!this.invoiceId) {
      this.errorFetchingInvoice = 'Error fetching invoice';
      const err = new Error('No invoice ID found');
      console.error(err);
      return;
    }

    if (this.isAPI) {
      this.fetchApiInvoice(this.invoiceId, this.tenantId);
    } else {
      this.fetchInvoice(this.invoiceId, this.tenantId);
      this.fetchTenant(this.tenantId);
    }
  }

  ngAfterViewInit() {
    this.acceptJSService.injectScript();
  }

  ngOnDestroy() {
    console.log('checkout.component ngOnDestroy');
    this.acceptJSService.removeScript();
    delete window.acceptJSResponseHandler;
  }

  get isAdmin(): boolean {
    return this.authService.isAdmin();
  }

  get hasOrder(): boolean {
    return !!(this.desktopInvoice || this.apiInvoice || this.desPurchase);
  }

  get orderTotal(): number {
    if (this.desPurchase) {
      return this.desPurchase.total_amount;
    }
    if (this.desktopInvoice) {
      return this.desktopInvoice.desktop_invoice.total_amount;
    }
    return this.apiInvoice!.api_invoice.total_amount;
  }

  get orderBase(): number {
    if (this.desPurchase) {
      return this.desPurchase.base_amount;
    }
    if (this.desktopInvoice) {
      return this.desktopInvoice.desktop_invoice.base_amount;
    }
    return this.apiInvoice!.api_invoice.base_amount;
  }

  get orderTax(): number {
    if (this.desPurchase) {
      return this.desPurchase.tax_amount;
    }
    if (this.desktopInvoice) {
      return this.desktopInvoice.desktop_invoice.tax_amount;
    }
    return this.apiInvoice!.api_invoice.tax_amount;
  }

  get orderNumber(): string {
    if (this.desPurchase) {
      return this.desPurchase.number;
    }
    if (this.desktopInvoice) {
      return this.desktopInvoice.desktop_invoice.number;
    }
    return this.apiInvoice!.api_invoice.number;
  }

  get orderCreatedAt(): string | undefined {
    if (this.desPurchase) {
      return this.desPurchase.created_at;
    }
    if (this.desktopInvoice) {
      return this.desktopInvoice.desktop_invoice.created_at;
    }
    return this.apiInvoice!.api_invoice.created_at;
  }

  get desPricePerThousand(): number {
    if (!this.desPurchase || !this.desPurchase.credits) {
      return 0;
    }
    return this.desPurchase.base_amount / (this.desPurchase.credits / 1000);
  }

  fetchDesPurchase(purchaseId: string, tenantId: string) {
    this.loadingInvoice = true;
    this.desCreditPurchasesService.getById(purchaseId).subscribe({
      next: (res) => {
        this.desPurchase = res.des_credit_purchase;
        this.tenant = res.tenant;
        this.loadingInvoice = false;
        if (this.desPurchase.completed) {
          this.errorFetchingInvoice = 'This purchase is already completed';
          return;
        }
        this.loadSavedPaymentMethods(this.desPurchase.tenant_id || tenantId);
      },
      error: (err) => this.handleInvoiceFetchError(err),
    });
  }

  get currentView() {
    return this.viewService.currentView;
  }

  fetchInvoice(invoiceId: string, tenantId: string) {
    this.loadingInvoice = true;

    this.invoiceService.getInvoiceDetails(invoiceId).subscribe({
      next: (res) => {
        console.log('Invoice details: ', res);
        this.desktopInvoice = res.invoice_details;

        if (this.desktopInvoice.desktop_invoice.completed) {
          // payment already completed, navigate to receipt page
          // do not trigger location change event, so that the back button works
          this.router.navigate(['receipt', invoiceId], {
            skipLocationChange: true,
          });
          return;
        }

        this.loadingInvoice = false;

        this.groupedLicenseInvoiceDetails =
          this.invoiceService.groupLicenseDetailsByRelation(
            this.desktopInvoice.license_details
          );
        this.groupedNotRenewingLicenses =
          this.invoiceService.groupLicensesByProduct(
            this.desktopInvoice.not_renewing_licenses
          );

        this.loadSavedPaymentMethods(tenantId);
      },
      error: (err) => this.handleInvoiceFetchError(err),
    });
  }

  fetchApiInvoice(invoiceId: string, tenantId: string) {
    this.loadingInvoice = true;
    this.invoiceService.getApiInvoiceDetails(invoiceId).subscribe({
      next: (res) => {
        console.log('Api Invoice response: ', res);
        this.apiInvoice = res.api_invoice_details;

        if (this.apiInvoice.api_invoice.completed) {
          this.router.navigate(
            ['api-receipt', this.apiInvoice.api_invoice.id],
            { skipLocationChange: true }
          );
          return;
        }

        this.loadingInvoice = false;

        this.apiProduct = res.api_invoice_details.api_product;
        this.tenant = res.tenant;

        this.loadSavedPaymentMethods(tenantId);
      },
      error: (err) => this.handleInvoiceFetchError(err),
    });
  }

  handleInvoiceFetchError(err: Error) {
    this.loadingInvoice = false;
    console.error(err);
    if (err instanceof HttpErrorResponse && err.status === 404) {
      this.errorFetchingInvoice = 'Invoice not found';
    } else {
      this.errorFetchingInvoice = 'Error fetching invoice';
    }
  }

  fetchTenant(tenantId: string) {
    this.loadingTenant = true;
    this.tenantsService.getTenantById(tenantId).subscribe({
      next: (res) => {
        this.loadingTenant = false;
        this.tenant = res.tenant;
      },
      error: (err) => {
        this.loadingTenant = false;
        console.error(err);
        this.errorFetchingTenant = 'Error fetching invoice details';
      },
    });
  }

  loadSavedPaymentMethods(tenantId: string) {
    this.loadingSavedPaymentMethods = true;
    this.paymentsService.getSavedPaymentMethods(tenantId).subscribe({
      next: (res) => {
        console.log('Saved payment methods: ', res);
        this.savedPaymentMethods = res.payment_profiles;
        this.savedPaymentMethods.sort((a, b) => {
          if (this.isAPI) {
            if (a.is_api_default) return -1;
            if (b.is_api_default) return 1;
          } else {
            if (a.is_default) return -1;
            if (b.is_default) return 1;
          }
          return 0;
        });
      },
      error: (err) => {
        this.loadingSavedPaymentMethods = false;
        console.error(err);
        this.errorFetchingSavedPaymentMethods = err;
      },
    });
  }

  getLicensePrice(license: DesktopLicenseWithProduct) {
    return this.invoiceService.getLicensePrice(license);
  }

  getLicenseType(license: DesktopLicenseWithProduct) {
    return this.licensesService.getType(license);
  }

  getSalesRepNames(salesReps?: SalesRep[]) {
    if (!salesReps || salesReps.length === 0) {
      return 'House';
    }
    return salesReps.map((r) => r.name).join(', ');
  }

  getDisplayTransactionMonth(dateString: string): Date {
    return this.invoiceService.getDisplayTransactionMonth(dateString);
  }

  showBackToCartButton() {
    return (
      this.desktopInvoice &&
      !this.desktopInvoice.desktop_invoice.completed &&
      !this.desktopInvoice.desktop_invoice.use_manual_values &&
      !this.isAPI &&
      !this.isDES &&
      !this.isAdmin
    );
  }

  payWithNewCard() {
    if (this.isDES && !this.desPurchase?.id) {
      return;
    }
    this.acceptPaymentButton.nativeElement.click();
  }

  acceptJSResponseHandler(response: any) {
    console.log('invoice paymentResponseHandler: ', response);

    if (this.paymentProcessing) {
      // already processing payment, do not process again
      return;
    }

    if (response.messages.resultCode === 'Ok') {
      if (this.isDES && !this.desPurchase?.id) {
        return;
      }
      this.paymentProcessing = true;
      console.log('submitting entered payment details');
      if (this.isDES) {
        this.paymentsService
          .processDesAuthorizeNetPayment({
            opaque_data: response.opaqueData,
            des_credit_purchase_id: this.desPurchase!.id,
          })
          .subscribe({
            next: (res) => this.handlePaymentResponse(res),
            error: (err) => {
              console.error('Error processing payment: ', err);
              this.paymentProcessing = false;
              this.handlePaymentError(err);
            },
          });
      } else if (this.isAPI) {
        this.paymentsService
          .processApiAuthorizeNetPayment({
            opaque_data: response.opaqueData,
            api_invoice_id: this.apiInvoice!.api_invoice.id,
          })
          .subscribe({
            next: (res) => this.handlePaymentResponse(res),
            error: (err) => {
              console.error('Error processing payment: ', err);
              this.paymentProcessing = false;
              this.handlePaymentError(err);
            },
          });
      } else {
        this.paymentsService
          .processAuthorizeNetPayment({
            opaque_data: response.opaqueData,
            invoice_id: this.desktopInvoice!.desktop_invoice.id,
          })
          .subscribe({
            next: (res) => this.handlePaymentResponse(res),
            error: (err) => {
              console.error('Error processing payment: ', err);
              this.paymentProcessing = false;
              this.handlePaymentError(err);
            },
          });
      }
    } else {
      // response from acceptJS is not valid
      console.error('Error response from acceptJS', response);
      this.paymentError =
        'There was an unexpected error processing your payment.';
    }
  }

  bankAccountTypeLabel(method: AuthorizeNetSavedPaymentMethod) {
    return this.paymentsService.getBankAccountTypeLabel(
      method.bank_account_type ?? ''
    );
  }

  payWithSelectedMethod() {
    if (!this.selectedSavedPaymentMethod) {
      console.error(new Error('No selected saved payment method'));
      return;
    }

    if (this.isDES && !this.desPurchase?.id) {
      return;
    }

    this.paymentProcessing = true;

    if (this.isDES) {
      this.paymentsService
        .processDesPaymentWithSavedMethod({
          des_credit_purchase_id: this.desPurchase!.id,
          saved_payment_method_id: this.selectedSavedPaymentMethod.id,
        })
        .subscribe({
          next: (res) => this.handlePaymentResponse(res),
          error: (err) => this.handlePaymentError(err),
        });
    } else if (this.isAPI) {
      this.paymentsService
        .processApiPaymentWithSavedMethod({
          api_invoice_id: this.apiInvoice!.api_invoice.id,
          saved_payment_method_id: this.selectedSavedPaymentMethod.id,
        })
        .subscribe({
          next: (res) => this.handlePaymentResponse(res),
          error: (err) => this.handlePaymentError(err),
        });
    } else {
      this.paymentsService
        .processPaymentWithSavedMethod({
          invoice_id: this.desktopInvoice!.desktop_invoice.id,
          saved_payment_method_id: this.selectedSavedPaymentMethod.id,
        })
        .subscribe({
          next: (res) => this.handlePaymentResponse(res),
          error: (err) => this.handlePaymentError(err),
        });
    }
  }

  handlePaymentResponse(
    response: DesktopPaymentResponse | ApiPaymentResponse | DesPaymentResponse
  ) {
    this.paymentProcessing = false;
    console.log('payment response: ', response);
    let status = '';
    let statusDescription = '';
    if ('des_payment' in response) {
      status = response.des_payment.status;
      statusDescription = response.des_payment.status_description;
    } else if ('api_payment' in response) {
      status = (response as ApiPaymentResponse).api_payment.status;
      statusDescription = (response as ApiPaymentResponse).api_payment
        .status_description;
    } else {
      status = (response as DesktopPaymentResponse).desktop_payment.status;
      statusDescription = (response as DesktopPaymentResponse).desktop_payment
        .status_description;
    }

    if (status === 'completed') {
      this.paymentError = '';
      if (this.isAPI) {
        this.invoiceService.pendingInvoice = undefined;
      }

      this.logPurchaseEvent();

      if (this.isDES) {
        const purchaseId =
          (response as DesPaymentResponse).des_credit_purchase?.id ||
          (response as DesPaymentResponse).des_payment.des_credit_purchase_id;
        this.router.navigate(['/des-receipt', purchaseId], {
          queryParams: {
            tenant_id: this.desPurchase?.tenant_id || this.tenantId,
          },
        });
      } else if (this.isAPI) {
        this.router.navigate(['/api-receipt', this.apiInvoice!.api_invoice.id]);
      } else {
        this.router.navigate([
          '/receipt',
          this.desktopInvoice!.desktop_invoice.id,
        ]);
      }
    } else if (status === 'failed') {
      this.paymentError = 'Payment failed.';
    } else if (status === 'declined') {
      this.paymentError = 'Payment method was declined.';
    } else if (status === 'held') {
      this.paymentError = 'Payment is being held. Please contact support.';
    } else {
      this.paymentError = 'Unexpected error processing payment.';
    }

    this.paymentStatusDescription = statusDescription;

    // refetch the pending invoice on the invoice service to update the cart
    this.invoiceService.fetchPendingInvoice();
  }

  handlePaymentError(err: HttpErrorResponse) {
    if (err instanceof HttpErrorResponse) {
      if (err.error && err.error.error) {
        if (err.error.error === 'unable to attempt payment') {
          // payment was not processed nor attempted. Likely a server error
          this.paymentError =
            'Unable to attempt payment. Please contact support.';
          return;
        } else if (err.error.error === 'payment post process failed') {
          // payment was attempted, but some post process failed
          this.paymentError =
            'Payment was submitted, but failed to update license renewal. Please contact support.';
          return;
        }
      }
    }
    this.paymentError = 'Unexpected error processing payment.';
  }

  logPurchaseEvent() {
    if (this.isDES && this.desPurchase) {
      this.firebaseService.logPurchaseEvent({
        value: this.desPurchase.total_amount,
        currency: 'USD',
        transaction_id: this.desPurchase.id,
        tax: this.desPurchase.tax_amount,
        items: [
          {
            item_id: 'des-credits',
            item_name: 'DES Credits',
            price: this.desPurchase.base_amount,
          },
        ],
      });
      return;
    }
    if (this.isAPI) {
      this.firebaseService.logPurchaseEvent({
        value: this.apiInvoice!.api_invoice.total_amount,
        currency: 'USD',
        transaction_id: this.apiInvoice!.api_invoice.id,
        tax: this.apiInvoice!.api_invoice.tax_amount,
        items: [
          {
            item_id: this.apiProduct!.id,
            item_name: this.apiProduct!.title,
            price: this.apiInvoice!.api_invoice.base_amount,
          },
        ],
      });
    } else {
      this.firebaseService.logPurchaseEvent({
        value: this.desktopInvoice!.desktop_invoice.total_amount,
        currency: 'USD',
        transaction_id: this.desktopInvoice!.desktop_invoice.id,
        tax: this.desktopInvoice!.desktop_invoice.tax_amount,
        items: this.desktopInvoice!.license_details.map((license) => ({
          item_id: license.license_with_product.product.id,
          item_name: license.license_with_product.product.title,
          item_variant: license.license_with_product.product_variant.id,
          item_variant_name:
            license.license_with_product.product_variant.description,
          price: license.base_amount,
        })),
      });
    }
  }
}
